import dayjs from 'dayjs';

import { MemoryDatePrecision } from 'src/Services/API/Memories.api';
import { MemorySeason, SEASON_MONTH } from 'src/Utils/memoryDate';

export type MemoryDatePrecisionChoice = MemoryDatePrecision | 'NONE';

export interface MemoryDateValue {
  precision: MemoryDatePrecisionChoice;
  day: Date | null;
  month: number | null; // 0-based
  season: MemorySeason | null;
  year: number | null;
  decade: number | null; // 1970
  approximate: boolean;
}

export const EMPTY_MEMORY_DATE: MemoryDateValue = {
  precision: MemoryDatePrecision.Day,
  day: null,
  month: null,
  season: null,
  year: null,
  decade: null,
  approximate: false,
};

const utcDate = (year: number, month: number, day: number) =>
  new Date(Date.UTC(year, month, day)).toISOString();

/** What the API expects; the server normalises the date to its precision anyway. */
export const toMemoryDatePayload = (
  value: MemoryDateValue
): {
  takenAt: string | null;
  takenAtPrecision: MemoryDatePrecision;
  takenAtApproximate: boolean;
} => {
  const none = {
    takenAt: null,
    takenAtPrecision: MemoryDatePrecision.Day,
    takenAtApproximate: false,
  };
  const withDate = (takenAt: string) => ({
    takenAt,
    takenAtPrecision: value.precision as MemoryDatePrecision,
    takenAtApproximate: value.approximate,
  });

  switch (value.precision) {
    case MemoryDatePrecision.Day:
      return value.day
        ? withDate(utcDate(value.day.getFullYear(), value.day.getMonth(), value.day.getDate()))
        : none;
    case MemoryDatePrecision.Month:
      return value.year !== null && value.month !== null
        ? withDate(utcDate(value.year, value.month, 1))
        : none;
    case MemoryDatePrecision.Season:
      return value.year !== null && value.season
        ? withDate(utcDate(value.year, SEASON_MONTH[value.season], 1))
        : none;
    case MemoryDatePrecision.Year:
      return value.year !== null ? withDate(utcDate(value.year, 6, 1)) : none;
    case MemoryDatePrecision.Decade:
      return value.decade !== null ? withDate(utcDate(value.decade + 5, 0, 1)) : none;
    default:
      return none;
  }
};

/** Rebuilds the form value from a saved memory. */
export const fromMemoryDate = (
  takenAt: string | null,
  precision: MemoryDatePrecision,
  approximate: boolean
): MemoryDateValue => {
  if (!takenAt) return { ...EMPTY_MEMORY_DATE, precision: 'NONE' };
  const date = dayjs.utc(takenAt);
  const month = date.month();
  const season: MemorySeason =
    month === 0 ? 'WINTER' : month === 3 ? 'SPRING' : month === 6 ? 'SUMMER' : 'AUTUMN';
  return {
    precision,
    day: new Date(date.year(), month, date.date()),
    month,
    season,
    year: date.year(),
    decade: Math.floor(date.year() / 10) * 10,
    approximate,
  };
};
